import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { domainsAPI, wordpressAPI } from '@hpanel/web-common/api/client'

/**
 * /client/wordpress/:id/staging — copy a WordPress site to a subdomain.
 *
 * The client picks the subdomain ("staging" by default). A routed page,
 * not a confirm() (CLAUDE.md §15): there is a field to fill in.
 */
export const WordPressStagingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const installId = Number(id)
  const navigate = useNavigate()
  const [domain, setDomain] = useState<string | null>(null)
  const [subdomain, setSubdomain] = useState('staging')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([wordpressAPI.get(installId), domainsAPI.list()])
      .then(([install, d]) => {
        setDomain(d.domains.find((x) => x.id === install.domain_id)?.name ?? null)
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[WordPressStagingPage] install lookup failed', e)
        setError(e instanceof Error ? e.message : 'Could not load this WordPress site')
      })
  }, [installId])

  const host = `${subdomain.toLowerCase()}.${domain ?? '…'}`

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const r = await wordpressAPI.createStaging(installId, subdomain)
      navigate('/client/wordpress', {
        state: {
          notice: r.reused
            ? `This site already has a staging copy at ${r.staging_url ?? 'its subdomain'}.`
            : `Staging copy ready at ${r.staging_url}.`,
        },
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[WordPressStagingPage] clone failed', err)
      setError(err instanceof Error ? err.message : 'Could not create the staging copy')
      setBusy(false)
    }
  }

  return (
    <div data-testid="wp-staging-page" className="max-w-xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Create a staging copy</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Copies the files and database of{' '}
          <span className="font-mono">{domain ?? `install #${installId}`}</span> to a separate site
          you can change safely. Your live site is not changed.
        </p>
      </div>
      <form onSubmit={onSubmit} className="card p-6 space-y-4">
        {error && (
          <div
            data-testid="wp-staging-error"
            role="alert"
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded p-3 text-sm"
          >
            {error}
          </div>
        )}
        <div>
          <label htmlFor="wp-staging-subdomain" className="label">Subdomain</label>
          <div className="flex items-center gap-2">
            <input
              id="wp-staging-subdomain"
              required
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value)}
              className="input w-48"
            />
            <span className="font-mono text-gray-600 dark:text-gray-400 break-all">.{domain ?? '…'}</span>
          </div>
          <p data-testid="wp-staging-url" className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            The copy will be at <span className="font-mono">https://{host}</span>. That name must point
            to this server in DNS.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button type="submit" disabled={busy || !domain} className="btn-primary disabled:opacity-50">
            {busy ? 'Copying… this can take a minute' : 'Create staging copy'}
          </button>
          <Link to="/client/wordpress" className="btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  )
}
